import { useQuery } from '@tanstack/react-query';
import { captureException } from '@sentry/react';
import { CustomError } from 'typescript/utilities/custom_error';
import {
  getProductAddOns as clientGetProductAddOns,
  listPickupOptions as clientListPickupOptions,
} from 'typescript/clients/solidshop/products_client';
import { QUERY_KEYS } from 'typescript/constants/react_query';

/**
 * Hook for getting product add-ons
 * @param productSlug - the product slug identifier
 * @param locale - the locale for the request
 * @param onLoadTimeTracked - callback with load time in ms when request completes
 * @returns query state for getting product add-ons
 */
const useAddOns = ({
  productSlug,
  locale,
  enabled = true,
  onLoadTimeTracked,
}: {
  productSlug: string;
  locale: string;
  enabled?: boolean;
  onLoadTimeTracked?: (loadTimeMs: number) => void;
}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_PRODUCT_ADD_ONS, { productSlug, locale }],
    queryFn: async () => {
      const startTime = performance.now();

      try {
        const result = await clientGetProductAddOns({
          locale,
          productSlug,
        });

        const endTime = performance.now();
        const loadTimeMs = endTime - startTime;

        // Call the callback with load time
        if (onLoadTimeTracked) {
          onLoadTimeTracked(loadTimeMs);
        }

        return result;
      } catch (error) {
        const endTime = performance.now();
        const loadTimeMs = endTime - startTime;

        // Track even on error
        if (onLoadTimeTracked) {
          onLoadTimeTracked(loadTimeMs);
        }

        throw error;
      }
    },
    enabled,
    throwOnError: (error) => {
      captureException(new CustomError(QUERY_KEYS.GET_PRODUCT_ADD_ONS, error), {
        extra: {
          productSlug,
          locale,
        },
      });
      return false; // Don't throw, just log
    },
  });
};

const useListPickupOptions = ({
  productSlug,
  postalCode,
  enabled = false,
}: {
  productSlug: string;
  postalCode: string;
  enabled?: boolean;
}) => {
  const { data: pickupOptionsData, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_OPTIONS_SLUG, { productSlug, postalCode }],
    queryFn: () =>
      clientListPickupOptions({
        productSlug,
        postalCode,
      }),
    enabled,
  });

  // only display the first three options
  // TODO: (SHOP-7677) move this to a limit param
  const limitedPickupOptionsData = pickupOptionsData?.slice(0, 3);

  return { pickupOptionsData: limitedPickupOptionsData, ...rest };
};

export { useAddOns, useListPickupOptions };
